// Date and time helpers for the forms. A `datetime-local` input holds `YYYY-MM-DDTHH:mm` and has no
// zone. An event is entered in the event's own time zone (a wall-clock time at the venue); a price
// validity window is entered in UTC and says so. Neither needs a date library: `Intl` knows the zones.

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

function pad(value: number, width = 2): string {
  return String(value).padStart(width, "0");
}

function parseLocal(local: string) {
  const match = LOCAL.exec(local);
  if (match === null) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  const probe = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const exact =
    probe.getUTCFullYear() === year &&
    probe.getUTCMonth() === month - 1 &&
    probe.getUTCDate() === day &&
    probe.getUTCHours() === hour &&
    probe.getUTCMinutes() === minute;
  return exact ? { year, month, day, hour, minute } : null;
}

/** The wall-clock time in `timeZone` at the instant `at`, read as if it were UTC. */
function wallClockAsUtc(at: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(new Date(at));
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute"),
    get("second"),
  );
}

/** An instant as `YYYY-MM-DDTHH:mm` on the wall clock of `timeZone`. `null` for an unknown zone. */
export function instantToZonedLocal(instant: Date, timeZone: string): string | null {
  if (!isValidTimeZone(timeZone)) return null;
  const wall = new Date(wallClockAsUtc(instant.getTime(), timeZone));
  return `${pad(wall.getUTCFullYear(), 4)}-${pad(wall.getUTCMonth() + 1)}-${pad(wall.getUTCDate())}T${pad(wall.getUTCHours())}:${pad(wall.getUTCMinutes())}`;
}

/**
 * The instant of a wall-clock time in `timeZone`. `null` for an unknown zone or a malformed value.
 * A time that does not exist (the hour skipped when the clocks go forward) maps to the instant just
 * after the gap; a time that exists twice maps to the first of them.
 */
export function zonedLocalToInstant(local: string, timeZone: string): Date | null {
  const parts = parseLocal(local);
  if (parts === null || !isValidTimeZone(timeZone)) return null;
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
  const firstOffset = wallClockAsUtc(asUtc, timeZone) - asUtc;
  let candidate = asUtc - firstOffset;
  const secondOffset = wallClockAsUtc(candidate, timeZone) - candidate;
  if (secondOffset !== firstOffset) candidate = asUtc - secondOffset;
  return new Date(candidate);
}

export function instantToUtcLocal(instant: Date): string {
  return instantToZonedLocal(instant, "UTC") ?? "";
}

export function utcLocalToInstant(local: string): Date | null {
  return zonedLocalToInstant(local, "UTC");
}
